import React, { useState } from 'react';
import BasicModal from '../../atoms/BasicModal';
import { useStores } from '../../../stores';
import moment from 'moment';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import clsx from 'clsx';
import { getFieldTypography } from '../AddBuildScenarioModal';
import { TextField } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';

export interface Reconcilation {
  _id: string;
  name: string;
}
export interface ReconcilationInfo {
  name: string;
  notes?: string;
}
interface ReconciliationModalProps {
  open: boolean;
  handleClose: () => void;

  existingReconciliations: Reconcilation[];
   
  handleExisting: (reconciliationId: string) => void;
   
  handleNew: (reconciliationInfo: ReconcilationInfo) => void;
}
type optionType = 'open' | 'create';

interface OptionCardProps {
  title: string;
  subTitle: string;
  value: optionType;
  isChecked: boolean;
   
  onSelect: (value: optionType) => void;
}

const useStyles = makeStyles()({
  bodyContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  flex: {
    display: 'flex',
    gap: '0.5rem',
  },
  optionCard: {
    gap: '1rem',
    width: '100%',
    padding: '1rem',
    borderRadius: '0.5rem',
    border: '2px solid #e5e7eb',
    transition: 'all 0.2s',
    cursor: 'pointer',
    '&:hover': {
      borderColor: '#d1d5db',
      backgroundColor: '#f9fafb',
    },
  },
  active: {
    borderColor: patientAccessTheme.palette.primary.main,
    backgroundColor: '#eff6ff',
    '&:hover': {
      borderColor: patientAccessTheme.palette.primary.main,
      backgroundColor: '#eff6ff',
    },
  },
});

const OptionCard: React.FC<OptionCardProps> = ({
  title,
  subTitle,
  value,
  isChecked,
  onSelect,
}) => {
  const { classes } = useStyles();
  return (
    <Grid
      className={clsx(
        classes.flex,
        classes.optionCard,
        isChecked && classes.active,
      )}
      onClick={() => onSelect(value)}
    >
      <input
        type="radio"
        name="reconciliation-option"
        value={value}
        checked={isChecked}
      />
      <Grid>
        <Typography
          textColor={patientAccessTheme.palette.text.highemphasis}
          fontType="medium"
          styleProps={{ marginBottom: '0.25rem' }}
        >
          {title}
        </Typography>
        <Typography
          fontSize="14px"
          textColor={patientAccessTheme.palette.text.lowemphasis}
        >
          {subTitle}
        </Typography>
      </Grid>
    </Grid>
  );
};

const ReconciliationModal: React.FC<ReconciliationModalProps> = ({
  open,
  handleClose,
  existingReconciliations = [],
  handleExisting,
  handleNew,
}) => {
  const { classes } = useStyles();
  const fiscalControlStore = useStores().fiscalControlStore;

  const [selectedOption, setSelectedOption] = useState<optionType>('open');
  const [selectedReconciliation, setSelectedReconciliation] = useState<string>(
    '',
  );
  const [
    reconciliationInfo,
    setReconciliationInfo,
  ] = useState<ReconcilationInfo>({
    name: '',
  });

  const start = moment(fiscalControlStore.startDate).format('MMMM YYYY');
  const end = moment(fiscalControlStore.endDate).format('MMMM YYYY');

  const handleSubmit = () => {
    if (selectedOption === 'open') {
      if (selectedReconciliation) {
        handleExisting(selectedReconciliation);
      }
    } else {
      if (reconciliationInfo.name) {
        handleNew(reconciliationInfo);
      }
    }
  };

  const handleModalClose = () => {
    setSelectedOption('open');
    setSelectedReconciliation('');
    setReconciliationInfo({ name: '', notes: '' });
    handleClose();
  };

  const handleReconcilationInfoChange = (e: any, fieldName: string) => {
    const value = e.target.value as string;
    setReconciliationInfo((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  if (!open) {
    return null;
  }

  return (
    <BasicModal
      open={open}
      handleClose={handleModalClose}
      title={'Manage Reconciliation'}
      isSaveButton={true}
      height="auto"
      saveBtnAction={handleSubmit}
      saveBtnText={selectedOption === 'open' ? 'Open' : 'Create'}
      width="600px"
    >
      <Grid container className={classes.bodyContainer}>
        <Grid className={classes.flex}>
          <Typography textColor={patientAccessTheme.palette.text.lowemphasis}>
            Date Range:
          </Typography>
          <Typography
            textColor={patientAccessTheme.palette.text.highemphasis}
            fontType="medium"
          >
            {start} - {end}
          </Typography>
        </Grid>
        <Grid className={classes.bodyContainer}>
          <OptionCard
            title={'Open Existing Reconciliation'}
            subTitle={'Continue working on a previously created reconciliation'}
            value={'open'}
            isChecked={selectedOption === 'open'}
            onSelect={setSelectedOption}
          />
          <OptionCard
            title={'Create New Reconciliation'}
            subTitle={'Start a new reconciliation for this period'}
            value={'create'}
            isChecked={selectedOption === 'create'}
            onSelect={setSelectedOption}
          />
        </Grid>
        <Grid className={classes.bodyContainer}>
          {selectedOption === 'open' ? (
            <Grid>
              {getFieldTypography(
                'Select Reconciliation Name',
                '0px 0px 10px 0px',
              )}
              <Dropdown
                label={
                  existingReconciliations.length > 0
                    ? 'Select Reconciliation'
                    : 'No Reconciliations available for the date range'
                }
                dropDownOptions={existingReconciliations.map(
                  (item) => item.name,
                )}
                dropDownValues={existingReconciliations.map((item) => item._id)}
                value={selectedReconciliation}
                setValue={setSelectedReconciliation}
                width="535px"
                selectStyles={{ width: '535px' }}
              />
            </Grid>
          ) : (
            <Grid>
              {getFieldTypography('Reconciliation Name *', '0px 0px 10px 0px')}
              <TextField
                value={reconciliationInfo.name}
                onChange={(e: any) => handleReconcilationInfoChange(e, 'name')}
                placeholder={'Enter Reconciliation Name'}
                height="36px"
                fullWidth
              />
              {getFieldTypography(
                'Notes/Remarks (Optional)',
                '20px 0px 10px 0px',
              )}
              <TextField
                value={reconciliationInfo.notes}
                onChange={(e: any) => handleReconcilationInfoChange(e, 'notes')}
                placeholder="Enter Notes/Remarks"
                height="36px"
                fullWidth
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </BasicModal>
  );
};

export default ReconciliationModal;
